import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { getChannelSources, getEventSources } from '@/actions/actions'
import {
  EventType,
  eventTypes,
  VideoSourceType,
} from '@/types/servicesTypes/configTypes'
import { showInfo } from '@/utils/utils'

export const useEventVideoSources = (event?: Nullable<EventType>) => {
  const [eventVideoSources, setEventVideoSources] =
    useState<Nullable<VideoSourceType[]>>()
  const [isEventActionMounted, setIsEventActionMounted] = useState(false)
  const { isRegisteredUser } = useAuth()

  useEffect(() => {
    if (!event) {
      setEventVideoSources(null)
      setIsEventActionMounted(true)
    }
    if (event) {
      getEventVideoSources(event, isRegisteredUser).finally(() =>
        setIsEventActionMounted(true),
      )
    }
  }, [event, isRegisteredUser])

  const getEventVideoSources = useCallback(
    async (event: EventType, isRegisteredUser: boolean) => {
      const isChannelWatchable = !!event.watchable
      const { isAccessible, isPremium, isPPVEvent } = getContentAccessFlags(
        event,
        null,
        isRegisteredUser,
      )

      const isPremiumEvent = isPremium || isPPVEvent

      let videoSources: VideoSourceType[] | undefined = isPremiumEvent
        ? undefined
        : event.video?.sources

      switch (event.type) {
        case eventTypes.VOD: {
          if (!isChannelWatchable || !isAccessible) {
            setEventVideoSources(null)
            return null
          }

          isDevMode &&
            videoSources &&
            showInfo('VideoSources from Event object')

          if (!videoSources) {
            try {
              const channelPlayback = await getEventSources(event.id)
              videoSources = channelPlayback.sources
            } catch {}
          }
          setEventVideoSources(videoSources)
          return videoSources
        }
        case eventTypes.CHANNEL: {
          if (!isChannelWatchable || !isAccessible) {
            setEventVideoSources(null)
            return null
          }

          isDevMode &&
            videoSources &&
            showInfo('VideoSources from Event object')

          if (!videoSources) {
            try {
              const channelPlayback = await getEventSources(event.id)
              videoSources = channelPlayback.sources
              isDevMode &&
                videoSources &&
                showInfo('VideoSources from backend (Event)')
            } catch {
              isDevMode && showInfo('Error get VideoSources from backend')
            }
          }

          if (!videoSources && event.channelId) {
            try {
              const channelPlayback = await getChannelSources(event.channelId)
              videoSources = channelPlayback.sources
              isDevMode &&
                videoSources &&
                showInfo('VideoSources from backend (channelEvent)')
            } catch {
              isDevMode && showInfo('Error get VideoSources from backend')
            }
          }
          setEventVideoSources(videoSources)
          return videoSources
        }
        default: {
          setEventVideoSources(null)
          return null
        }
      }
    },
    [],
  )

  return { eventVideoSources, isEventActionMounted, getEventVideoSources }
}
